/**
 * ContactRow Component
 *
 * Displays a contact in row format using BaseRow layout.
 * Shows contact handles, company, role, tags, and quick actions.
 */

import React, { useMemo, useState } from 'react';
import { User } from 'lucide-react';
import { BaseRow } from '../common/base/BaseRow';
import { PrimaryAction, EntityAvatar } from '../common/base';
import { LinkManager } from '../../services/actions/LinkManager';
import { getAvailableActions } from '../common/registry/ActionRegistry';
import type { Contact } from '@business-assistant/shared/domain';

// =============================================================================
// TYPES
// =============================================================================

export interface ContactRowProps {
  entity: Contact;
  onTitleClick?: (contact: Contact) => void;
  onActionClick?: (actionType: string, contactId: string) => void;
  onAvatarClick?: (name: string, id?: string) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function ContactRow({ entity: contact, onTitleClick, onActionClick, onAvatarClick }: ContactRowProps) {
  // Primary action state (favorite)
  const [isFavorite, setIsFavorite] = useState(false);

  // Get linked action counts for icon coloring
  const linkedCounts = useMemo(() => {
    return LinkManager.getLinkedActionCounts(contact.id);
  }, [contact.id]);

  // Left slot - Primary Action + Contact Avatar
  const leftSlot = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <PrimaryAction
        type="star"
        active={isFavorite}
        onChange={(favorite) => {
          setIsFavorite(favorite);
          onActionClick?.('favorite', contact.id);
        }}
      />
      <EntityAvatar
        items={{ id: contact.id, name: contact.displayName }}
        type="auto"
        size={24}
        onClick={onAvatarClick ? (item) => onAvatarClick(item.name, item.id) : undefined}
      />
    </div>
  );

  // Get available actions for contacts (uses standard fast actions + entity-specific)
  const availableActions = useMemo(() => {
    return getAvailableActions(contact.id);
  }, [contact.id]);

  // Build meta content
  const metaContent = useMemo(() => {
    const parts: React.ReactNode[] = [];

    // Company and role
    if (contact.company) {
      parts.push(
        <span key="company" style={styles.metaItem}>
          {contact.company}
          {contact.role && ` · ${contact.role}`}
        </span>
      );
    } else if (contact.role) {
      parts.push(
        <span key="role" style={styles.metaItem}>
          {contact.role}
        </span>
      );
    }

    // Contact handles (show available communication channels)
    const handles: React.ReactNode[] = [];
    if (contact.handles.email) {
      handles.push(
        <span key="email" style={styles.handleBadge} title={contact.handles.email}>
          📧
        </span>
      );
    }
    if (contact.handles.phone) {
      handles.push(
        <span key="phone" style={styles.handleBadge} title={contact.handles.phone}>
          📞
        </span>
      );
    }
    if (contact.handles.whatsapp) {
      handles.push(
        <span key="whatsapp" style={styles.handleBadge} title={contact.handles.whatsapp}>
          💬
        </span>
      );
    }
    if (contact.handles.telegram) {
      handles.push(
        <span key="telegram" style={styles.handleBadge} title={contact.handles.telegram}>
          ✈️
        </span>
      );
    }

    if (handles.length > 0) {
      parts.push(
        <div key="handles" style={styles.handlesContainer}>
          {handles}
        </div>
      );
    }

    // Tags
    if (contact.tags && contact.tags.length > 0) {
      parts.push(
        <div key="tags" style={styles.tagsContainer}>
          {contact.tags.slice(0, 3).map((tag) => (
            <span key={tag} style={styles.tag}>
              {tag}
            </span>
          ))}
          {contact.tags.length > 3 && (
            <span style={styles.tag}>+{contact.tags.length - 3}</span>
          )}
        </div>
      );
    }

    return (
      <div style={styles.metaContainer}>
        {parts.map((part, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <span style={styles.separator}>·</span>}
            {part}
          </React.Fragment>
        ))}
      </div>
    );
  }, [contact]);

  // Icon
  const icon = (
    <User size={16} style={{ color: '#3b82f6' }} />
  );

  return (
    <BaseRow
      entity={{ ...contact, title: contact.displayName }}
      entityType="contact"
      leftSlot={leftSlot}
      icon={icon}
      metaContent={metaContent}
      availableActions={availableActions}
      linkedActionCounts={linkedCounts}
      onTitleClick={onTitleClick}
      onActionClick={onActionClick}
    />
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  metaContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,
  metaItem: {
    color: '#666',
  } as React.CSSProperties,
  separator: {
    color: '#ccc',
    margin: '0 2px',
  } as React.CSSProperties,
  handlesContainer: {
    display: 'flex',
    gap: '4px',
  } as React.CSSProperties,
  handleBadge: {
    fontSize: '12px',
    cursor: 'default',
  } as React.CSSProperties,
  tagsContainer: {
    display: 'flex',
    gap: '4px',
  } as React.CSSProperties,
  tag: {
    padding: '1px 6px',
    borderRadius: '3px',
    fontSize: '11px',
    background: '#f0f0f0',
    color: '#666',
  } as React.CSSProperties,
};
